import { useState } from 'react';
import * as React from 'react';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { Attachment } from '@sdk/attachments/types';
import { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import { ENTER_KEY_CODE } from '@sdk/common/constants';
import { Persona } from '@sdk/personaCollection/types/Persona';
import ModalFooterActionButtons from '@sdk/components/ui/modal/ModalFooterActionButtons';
import ModalBodyWithHeader from '@sdk/components/ui/modal/ModalBodyWithHeader';
import Modal from '@sdk/components/ui/modal/Modal';

import TextEditorWithContext from '../TextEditorWithContext';

export type Props = {
  isOpen: boolean;
  onClose: () => void;
  initialValue: string;
  initialAttachments?: Attachment[];
  personas?: Persona[];
  currentUser: AuthUserType;
  onSubmit?: (contentObj: { text: string; attachments: Attachment[] }) => void;
  titleText?: string;
  titleIcon?: JSX.Element;
  preContent?: JSX.Element;
  postContent?: JSX.Element;
  hiddenToolbarControls?: boolean;
};
const modalConfig = { noBody: true };

const TextEditorModal: React.FC<React.PropsWithChildren<Props>> = ({
  isOpen,
  onClose,
  initialValue,
  initialAttachments,
  personas,
  onSubmit,
  currentUser,
  titleText,
  titleIcon,
  preContent,
  postContent,
  hiddenToolbarControls,
}) => {
  const [content, setContent] = useState({ text: initialValue, attachments: initialAttachments });

  const isSubmitActive = !!content.text?.trim() || !!content.attachments?.length;

  if (!isOpen) {
    return null;
  }

  return (
    <Modal config={modalConfig} isVisible={isOpen} onDismiss={onClose} zIndex={1045}>
      <div
        onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>): void => {
          if ((event.ctrlKey || event.metaKey) && event.keyCode === ENTER_KEY_CODE) {
            // Ctrl/Cmd + Enter
            event.preventDefault();
            onSubmit(content);
          }
        }}>
        <ModalBodyWithHeader
          width={620}
          titleThemeColor={ThemeColorType.primary}
          titleText={titleText}
          titleIcon={titleIcon}
          close={onClose}>
          {!!preContent && preContent}

          <TextEditorWithContext
            onContentChange={setContent}
            initialValue={initialValue}
            initialAttachments={initialAttachments}
            currentUser={currentUser}
            personas={personas}
            hiddenToolbarControls={hiddenToolbarControls}
            submitOnEnter={false}
            gifPickerEnabled={false}
            hideSubmit
            resizable
          />

          {!!postContent && postContent}

          <ModalFooterActionButtons
            isSubmitActive={isSubmitActive}
            cancel={onClose}
            submit={async (): Promise<void> => onSubmit(content)}
          />
        </ModalBodyWithHeader>
      </div>
    </Modal>
  );
};

export default TextEditorModal;
